import { TableFullPermissionFormData, TablePermissionsFormData } from '@doss/common-all'
import { isEqual } from 'lodash'
import { PermissionInput, PermissionType } from '../../common/generated/apollo-operations'
import {
  columnPermissionHandlers,
  convertParentTableFormDataToPermissionInputs,
  createInitialTablePermissionSchema,
  fullTablePermissionHandlers,
  generalTablePermissionHandlers,
  processChildTablePermissions,
  tabPermissionHandlers,
} from '../../components/permissions/utils'
import {
  filterByType,
  isFullTablePermission,
  isGeneralTablePermission,
  isTableColumnPermission,
  isTablePermission,
  isTableTabPermission,
} from '../permissions/types'
import { RoleWithTablePermissions, SimpleTable } from './types'

const TABLE_PERMISSION_KEYS: (keyof TablePermissionsFormData)[] = [
  'addRow',
  'deleteRow',
  'configureTabs',
  'configurePivotTables',
]

/** Returns the column permission row, or `undefined` if `columnId` is not present in the map. */
function columnPermissionEntry(
  columnPermissions: TableFullPermissionFormData['columnPermissions'],
  columnId: string,
): { view: boolean; edit: boolean } | undefined {
  if (!Object.prototype.hasOwnProperty.call(columnPermissions, columnId)) {
    return undefined
  }
  return columnPermissions[columnId]
}

/** True when the value has the full `TableFullPermissionFormData` shape (guards partial RHF watch values). */
export function isCompleteRoleTablePermissions(
  value: unknown,
): value is TableFullPermissionFormData {
  if (!value || typeof value !== 'object') {
    return false
  }
  const v = value as Partial<TableFullPermissionFormData>
  return (
    v.columnPermissions !== undefined &&
    typeof v.columnPermissions === 'object' &&
    v.tablePermissions !== undefined &&
    typeof v.tablePermissions === 'object' &&
    v.tabPermissions !== undefined &&
    typeof v.tabPermissions === 'object' &&
    v.childTables !== undefined &&
    typeof v.childTables === 'object'
  )
}

/** Table permission keys that were true in `before` and are false in `after`. */
export function getDowngradedTablePermissionKeys(
  before: TablePermissionsFormData,
  after: TablePermissionsFormData,
): (keyof TablePermissionsFormData)[] {
  return TABLE_PERMISSION_KEYS.filter((k) => Boolean(before[k]) && !after[k])
}

/** Warning payload: losing add row via required-column view/edit removal, plus other table-level removals. */
export type FieldPermissionRoleImpact = {
  /** True when view or edit was removed on a required column and that drops an add-row grant they had. */
  losesRequiredColumnRowOps: boolean
  removedTablePermissions: (keyof TablePermissionsFormData)[]
}

/**
 * Compares two role table permission states for one column edit. Returns null when there is no
 * meaningful warning (no add-row loss from required column, and no removed table-level flags).
 */
export function getFieldPermissionRoleImpact(
  before: TableFullPermissionFormData,
  after: TableFullPermissionFormData,
  context: {
    columnId: string
    editPermissionRequiredForRowOps: boolean
  },
): FieldPermissionRoleImpact | null {
  if (!isCompleteRoleTablePermissions(before) || !isCompleteRoleTablePermissions(after)) {
    return null
  }

  const colBefore = columnPermissionEntry(before.columnPermissions, context.columnId)
  const colAfter = columnPermissionEntry(after.columnPermissions, context.columnId)

  const losesRequiredColumnRowOps = Boolean(
    context.editPermissionRequiredForRowOps &&
    colBefore !== undefined &&
    colAfter !== undefined &&
    before.tablePermissions.addRow === true &&
    after.tablePermissions.addRow === false &&
    ((colBefore.edit === true && colAfter.edit === false) ||
      (colBefore.view === true && colAfter.view === false)),
  )

  const removedTablePermissions = getDowngradedTablePermissionKeys(
    before.tablePermissions,
    after.tablePermissions,
  ).filter((k) => k !== 'deleteRow')
  // Omit delete row: field-permissions UI warnings only cover column + inherited table flags.

  if (!losesRequiredColumnRowOps && removedTablePermissions.length === 0) {
    return null
  }

  return {
    losesRequiredColumnRowOps,
    removedTablePermissions,
  }
}

/** Builds `PermissionInput[]` for updating a role's table permissions (parent + child rows). */
export function convertFormDataToPermissionInput(
  tableId: string,
  permissions: TableFullPermissionFormData,
): PermissionInput[] {
  const permissionInputs: PermissionInput[] = []
  convertParentTableFormDataToPermissionInputs(tableId, permissions, permissionInputs)
  // Backend replaces table permissions; emit child rows so unchanged child grants are re-sent.
  processChildTablePermissions(permissions.childTables, permissionInputs, {
    includeAllPermissions: false,
  })

  return permissionInputs
}

export type RolePermissionsMap = Record<string, TableFullPermissionFormData>

export type FieldPermissionType = PermissionType.VIEW_COLUMN | PermissionType.EDIT_CELL

/**
 * Applies one column's view or edit checkbox change, including table-level side effects.
 * Matches `TableColumnPermissions` / `TableTabsPermissions` in `components/permissions/table/`:
 * revoking view clears `configurePivotTables` only (not `configureTabs`); revoking view or edit on a
 * required column clears `addRow` when `editPermissionRequiredForRowOps` (same predicate as
 * `isEditPermissionRequiredForAddRow` / role form).
 *
 * Processing order:
 * 1. Update `tablePermissions` for revokes: clearing edit or view on a required column clears `addRow`;
 *    clearing view clears `configurePivotTables` only.
 * 2. Merge the toggled column into `columnPermissions`.
 * 3. If view is turned back on, copy `configurePivotTables` from `initialPermissions` when the saved
 *    column had view (revoke forces `edit` off; restore pivot to match saved table flags).
 * 4. If the column matches `initialPermissions` for this column exactly, replace parent `tablePermissions`
 *    with the snapshot (full revert of side effects).
 *
 * `initialPermissions` is the last loaded server state for this role/table (field permissions tab).
 */
export function computeFieldPermissionChange(
  currentPermissions: TableFullPermissionFormData,
  columnId: string,
  permissionType: FieldPermissionType,
  checked: boolean,
  editPermissionRequiredForRowOps: boolean,
  initialPermissions?: TableFullPermissionFormData,
): TableFullPermissionFormData {
  const updatedTablePermissions = { ...currentPermissions.tablePermissions }
  const updatedChildTables = { ...currentPermissions.childTables }

  // `permissionType` is only VIEW_COLUMN | EDIT_CELL; both can revoke addRow for required columns.
  if (!checked && editPermissionRequiredForRowOps) {
    updatedTablePermissions.addRow = false
  }
  if (permissionType === PermissionType.VIEW_COLUMN && !checked) {
    updatedTablePermissions.configurePivotTables = false
  }

  const updatedColumnPermissions = Object.entries(currentPermissions.columnPermissions).reduce<
    Record<string, { view: boolean; edit: boolean }>
  >((acc, [colId, columnPermission]) => {
    if (colId === columnId) {
      if (permissionType === PermissionType.EDIT_CELL && checked) {
        return { ...acc, [colId]: { edit: checked, view: true } }
      }
      if (permissionType === PermissionType.VIEW_COLUMN && !checked) {
        return { ...acc, [colId]: { edit: false, view: checked } }
      }
      const permissionKey = permissionType === PermissionType.VIEW_COLUMN ? 'view' : 'edit'
      return {
        ...acc,
        [colId]: { ...columnPermission, [permissionKey]: checked },
      }
    }
    return { ...acc, [colId]: columnPermission }
  }, {})

  const initialColumn = initialPermissions
    ? columnPermissionEntry(initialPermissions.columnPermissions, columnId)
    : undefined
  const updatedColumn = columnPermissionEntry(updatedColumnPermissions, columnId)
  // Restore pivot flag when view is re-enabled without a full column match (see docblock).
  if (
    permissionType === PermissionType.VIEW_COLUMN &&
    checked &&
    initialPermissions &&
    initialColumn !== undefined &&
    initialColumn.view === true &&
    updatedColumn !== undefined &&
    updatedColumn.view === true
  ) {
    updatedTablePermissions.configurePivotTables =
      initialPermissions.tablePermissions.configurePivotTables
  }

  // Full column match to snapshot: restore all parent table-level flags from the server state.
  if (
    initialPermissions &&
    initialColumn !== undefined &&
    updatedColumn !== undefined &&
    isEqual(initialColumn, updatedColumn)
  ) {
    return {
      ...currentPermissions,
      tablePermissions: { ...initialPermissions.tablePermissions },
      columnPermissions: updatedColumnPermissions,
      childTables: updatedChildTables,
    }
  }

  return {
    ...currentPermissions,
    tablePermissions: updatedTablePermissions,
    childTables: updatedChildTables,
    columnPermissions: updatedColumnPermissions,
  }
}

/** Maps each role id to `TableFullPermissionFormData` for one table from GraphQL `tablePermissions`. */
export function convertTablePermissionsToMap(
  roles: RoleWithTablePermissions[],
  table: SimpleTable,
): RolePermissionsMap {
  const permissionsMap: RolePermissionsMap = {}

  roles.forEach((role) => {
    const schema = createInitialTablePermissionSchema(table)
    const validTablePermissions = filterByType(role.tablePermissions, isTablePermission)

    validTablePermissions.forEach((permission) => {
      if (isFullTablePermission(permission)) {
        const handler = fullTablePermissionHandlers[permission.permissionType]
        const permissionTableId = permission.table.id
        if (permissionTableId === table.id) {
          handler(schema)
        } else if (permissionTableId in schema.childTables) {
          handler(schema.childTables[permissionTableId])
        }
      } else if (isGeneralTablePermission(permission)) {
        // General table permissions are stored on the parent only; child rows inherit on save.
        const handler = generalTablePermissionHandlers[permission.permissionType]
        handler(schema)
      } else if (isTableColumnPermission(permission)) {
        const handler = columnPermissionHandlers[permission.permissionType]
        handler(schema, permission)
      } else if (isTableTabPermission(permission)) {
        const handler = tabPermissionHandlers[permission.permissionType]
        handler(schema, permission)
      }
    })

    permissionsMap[role.id] = schema
  })

  return permissionsMap
}
